import { APIClientServices } from "./APIClientServices";

// ── DTOs (alinhados com backend PublicDTO) ────────────────────────

export interface MedPatientAgentDTO {
	id: string;
	name: string;
	agentType: string;
	description: string | null;
	avatarUrl: string | null;
	isActive: boolean;
}

export interface MedPatientMessageDTO {
	id: string;
	role: string;
	content: string;
	createdAt: string;
}

export interface MedPatientConversationDTO {
	conversationId: string;
	messages: MedPatientMessageDTO[];
}

/** Alinhado com PublicMedAppointmentDTO do backend */
export interface MedAppointmentDTO {
	id: string;
	workspaceId: string;
	patientId: string;
	doctorId: string;
	specialtyId: string | null;
	title: string;
	description: string | null;
	scheduledAt: string;
	durationMinutes: number;
	status: string;
	notes: string | null;
	createdAt: string;
	updatedAt: string;
}

/** Prescrições são MedRecords com recordType="prescription" */
export interface MedPrescriptionDTO {
	id: string;
	workspaceId: string;
	patientId: string;
	doctorId: string;
	appointmentId: string | null;
	recordType: string;
	title: string;
	content: string;
	structuredData: Record<string, any> | null;
	attachments: string[];
	createdAt: string;
	updatedAt: string;
}

/** Histórico de saúde são MedRecords genéricos */
export interface MedHealthRecordDTO {
	id: string;
	workspaceId: string;
	patientId: string;
	doctorId: string;
	appointmentId: string | null;
	recordType: string;
	title: string;
	content: string;
	structuredData: Record<string, any> | null;
	attachments: string[];
	createdAt: string;
	updatedAt: string;
}

export interface MedPatientAppointmentDetailDTO {
	appointment: MedAppointmentDTO;
	doctorName: string;
	records: MedHealthRecordDTO[];
}

// ── Service ───────────────────────────────────────────────────────

export class MedPatientServices {
	private static readonly BASE_ROUTE = "v1/medical";

	// ── Agentes IA para Paciente ──────────────────────────────────

	static async listAgents(): Promise<MedPatientAgentDTO[]> {
		return APIClientServices.get<MedPatientAgentDTO[]>(`${this.BASE_ROUTE}/patient/agents`);
	}

	// ── Chat com Agente ───────────────────────────────────────────

	static async sendMessage(agentId: string, content: string): Promise<{ userMessage: MedPatientMessageDTO; assistantMessage: MedPatientMessageDTO }> {
		return APIClientServices.post(`${this.BASE_ROUTE}/patient/agents/${agentId}/messages`, { content });
	}

	static async getConversation(agentId: string): Promise<MedPatientConversationDTO> {
		return APIClientServices.get<MedPatientConversationDTO>(`${this.BASE_ROUTE}/patient/agents/${agentId}/conversation`);
	}

	// ── Consultas ─────────────────────────────────────────────────

	static async listAppointments(): Promise<MedAppointmentDTO[]> {
		return APIClientServices.get<MedAppointmentDTO[]>(`${this.BASE_ROUTE}/patient/appointments`);
	}

	// ── Receitas ──────────────────────────────────────────────────

	static async listPrescriptions(): Promise<MedPrescriptionDTO[]> {
		return APIClientServices.get<MedPrescriptionDTO[]>(`${this.BASE_ROUTE}/patient/prescriptions`);
	}

	// ── Histórico ─────────────────────────────────────────────────

	static async listHealthRecords(): Promise<MedHealthRecordDTO[]> {
		return APIClientServices.get<MedHealthRecordDTO[]>(`${this.BASE_ROUTE}/patient/records`);
	}

	// ── Detalhe da Consulta ───────────────────────────────────────

	static async getAppointmentDetail(appointmentId: string): Promise<MedPatientAppointmentDetailDTO> {
		return APIClientServices.get<MedPatientAppointmentDetailDTO>(`${this.BASE_ROUTE}/patient/appointments/${appointmentId}`);
	}
}
